import React, { useState, useEffect } from "react";
import {
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import AddButton from "@Components/Buttons/AddButton";
import useDeleteItem from "@Hooks/useDeleteItem";
import axiosInstance from "@API/axios";
import {
  EXIT_REQUESTS,
  CANCELED_EXIT_REQUESTS,
  EXIT_REQUEST_PDF,
  REGIONS_URL,
  COUNTRIES_URL,
  GET_CATEGORIES_URL,
} from "@API/APIRoutes";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import { Edit } from "@mui/icons-material";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import InfoIcon from '@mui/icons-material/Info';
import { ThemeProvider } from "@mui/system";
import MeatballsMenu from "@Components/MeatballsMenu/MeatballsMenu";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericExitRequestListing from "../../../GenericExitRequestListing/GenericExitRequestListing";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import usePaginate from "@Hooks/usePaginate";
import { getStatusColor } from "@Utils/Validations/HelperFunctions/StyleFunction";
import {
  stringCustomOperators,
  numberCustomOperators,
  selectCustomOperators,
} from "@Utils/Validations/HelperFunctions/CustomFilters";
import useSendGetRequest from "@Hooks/useSendGetRequest";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import {
  TagsContainer,
  TagItem,
} from "../../../Admin/ListingItems/ListingItems";

function GenericPendingExitRequests({
  API,
  exportApi,
  title,
  all = false,
  readOnly = false,
  cancelled = true,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [cancelWarningContent, setCancelWarningContent] = useState("");
  const [showPdfPopup, setShowPdfPopup] = useState(false);
  const [selectedPdfId, setSelectedPdfId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const { loading: cancelReqLoading, deleteItem: cancelOrder } =
    useDeleteItem();
  const { loading: pdfLoading, getData: getPdf } = useSendGetRequest(
    true,
    true,
  ); // Enable binary mode for PDF
  const { getData: getRegions } = useSendGetRequest();
  const { getData: getCountries } = useSendGetRequest();
  const { getData: getCategories } = useSendGetRequest();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const { loading: pageLoading, getPage } = usePaginate();
  const errorMessage = "Failed to fetch Requests !";
  const isCancelled = false;
  const navigate = useNavigate();

  // Region & Country data for filters
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const user = useSelector((state) => state.user);

  // Fetch regions and countries on mount for filter dropdowns
  useEffect(() => {
    getRegions(REGIONS_URL).then((data) => {
      if (data) {
        setRegions(data);
      }
    });
    getCountries(COUNTRIES_URL).then((data) => {
      if (data) {
        setCountries(data);
      }
    });
    getCategories(GET_CATEGORIES_URL).then((data) => {
      if (data) {
        setCategories(data);
      }
    });
  }, []);

  // Format date to readable format
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return dayjs(dateStr).format("MM/DD/YYYY");
  };
  const downloadPdf = async (id) => {
    try {
      const response = await getPdf(`${EXIT_REQUEST_PDF}/${id}`);

      // Ensure we have valid response data
      if (!response) {
        console.error("No response data received");
        return;
      }

      // The response is already a Blob when using responseType: 'blob'
      const blob =
        response instanceof Blob
          ? response
          : new Blob([response], { type: "application/pdf" });

      // Check if blob is valid
      if (blob.size === 0) {
        console.error("Received empty PDF data");
        return;
      }

      // Create download URL
      const url = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `exit_request_${id}.pdf`);
      link.style.display = "none"; // Hide the link

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("PDF download initiated successfully");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      // You might want to show a user-friendly error message here
    }
  };

  //********************* PDF POPUP *********************//
  const openPdfPopup = async (id) => {
    // Open popup immediately
    setSelectedPdfId(id);
    setShowPdfPopup(true);
    setPdfUrl(null); // Reset URL to show loading state

    try {
      const response = await getPdf(`${EXIT_REQUEST_PDF}/${id}`);

      if (!response) {
        console.error("No response data received");
        return;
      }

      const blob =
        response instanceof Blob
          ? response
          : new Blob([response], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error("Failed to load PDF:", error);
      // Keep popup open but show error state
    }
  };

  const closePdfPopup = () => {
    setShowPdfPopup(false);
    setSelectedPdfId(null);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleDownloadFromPopup = () => {
    if (selectedPdfId) {
      downloadPdf(selectedPdfId);
    }
  };

  //********************* CANCEL POPUP *********************//
  const closePopup = () => {
    setShowCancelPopup(false);
    setCancelWarningContent("");
  };

  const openDeletePopup = async (OrderId) => {
    setSelectedId(OrderId);

    // Check if exit request has impact analyses or exit plans
    try {
      const response = await axiosInstance.get(
        `${EXIT_REQUESTS}/cancel-check/${OrderId}`,
      );
      const { hasImpact, responsesCount, plansCount } = response.data;

      if (hasImpact) {
        let warningMessage = "⚠️ This exit request has:\n\n";
        if (responsesCount > 0) {
          warningMessage += `• ${responsesCount} submitted impact ${responsesCount === 1 ? "analysis" : "analyses"}\n`;
        }
        if (plansCount > 0) {
          warningMessage += `• ${plansCount} exit ${plansCount === 1 ? "plan" : "plans"}\n`;
        }
        warningMessage +=
          "\nCancelling this exit request will permanently delete all associated impact analyses and exit plans.";
        setCancelWarningContent(warningMessage);
      } else {
        setCancelWarningContent("");
      }
    } catch (error) {
      console.error("Error checking cancellation impact:", error);
      setCancelWarningContent("");
    }

    setShowCancelPopup(true);
  };

  const confirmCancelCallBack = (canceledReqId) => {
    setRows((prevData) => prevData.filter((req) => req.id !== canceledReqId));
    closePopup();
  };

  const ConfirmCancel = (reqId) => {
    cancelOrder(
      reqId,
      confirmCancelCallBack,
      CANCELED_EXIT_REQUESTS,
      `EXIT REQUEST Canceled Successfully!`,
      `Failed to Cancel EXIT REQUEST!`,
    );
  };

  //********************* TABLE PROPS *********************//
  const meatballsMenuOptions = (id, status) => {
    const options = [];

    // Only show edit button if status is not CLOSED
    if (status !== "CLOSED") {
      options.push({
        label: "Edit Exit Request",
        icon: <Edit />,
        color: "primary",
        action: () =>
          navigate(DefaultRoutes.editExitRequest.replace(":reqId", id)),
      });
    }

    options.push({
      label: "Cancel Exit Request",
      icon: <CancelRoundedIcon />,
      color: "red",
      action: () => {
        openDeletePopup(id);
      },
    });

    return options;
  };

  // Fetch data from API - sends all filter items as a list
  const fetchPage = (queryOptions, filterModel) => {
    const filterItems = (filterModel?.items || []).map((item) => {
      const cleaned = { ...item };
      if (cleaned.value && typeof cleaned.value === "string") {
        cleaned.value = cleaned.value.trim();
        if (cleaned.field === "state") {
          cleaned.value = cleaned.value.replace(/\s+/g, "_");
        }
      }
      return cleaned;
    }).filter((item) => item.field && item.operator);

    const url = cancelled ? `${API}` + `/` + `${isCancelled}` : `${API}`;
    getPage(url, errorMessage, queryOptions, filterItems).then((data) => {
      if (data) {
        setRows(data.content);
        setRowCount(data.totalElements);
      }
    });
  };

  // The export endpoint takes the same cancelled-status suffix the listing call does, so the file
  // covers the same set of requests the grid is showing.
  const exportUrl = exportApi
    ? cancelled
      ? `${exportApi}/${isCancelled}`
      : exportApi
    : null;

  //********************* TABLE PROPS *********************//

  const columns = [
    {
      field: "id",
      headerName: "Request ID",
      flex: 1.0,
      minWidth: 80,
      type: "number",
      align: 'left',
      filterOperators: numberCustomOperators,
    },
    {
      field: "ingredient",
      headerName: "Ingredient",
      flex: 1.2,
      minWidth: 150,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <div>{params.row.ingredient.name}</div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.ingredient.casNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "ingredientName",
      headerName: "Ingredient Name",
      flex: 1.2,
      minWidth: 130,
      type: "string",
      valueGetter: (params) => params.row.ingredient.name,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "ingredientCasNumber",
      headerName: "CAS Number",
      flex: 1,
      minWidth: 110,
      type: "string",
      valueGetter: (params) => params.row.ingredient.casNumber,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "userEmail",
      headerName: "Created By",
      flex: 1.5,
      minWidth: 160,
      renderCell: (params) => <div>{params?.row?.user?.email}</div>,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "restrictionType",
      headerName: "Restriction",
      flex: 1.3,
      minWidth: 130,
      type: "string",
      filterOperators: stringCustomOperators,
    },
     {
      field: "regionId",
      headerName: "Region",
      flex: 1.2,
      minWidth: 130,
      type: "singleSelect",
      valueOptions: regions.map((r) => ({ value: String(r.id), label: r.name })),
      filterOperators: selectCustomOperators,
      renderCell: (params) => {
        const regions = params.row.regions;
        let items = [];
        if (Array.isArray(regions) && regions.length > 0) {
          items = [...new Set(regions.filter(Boolean))];
        } else {
          const rawCountries = params.row["countries"] || [];
          items = [...new Set(rawCountries.map(i => i?.regionName).filter(Boolean))];
        }

        return (
          <TagsContainer>
            {items.map((item, index) => (
              <TagItem key={index}>{item}</TagItem>
            ))}
          </TagsContainer>
        );
      },
      sortable: false,
    },
    {
      field: "countryId",
      headerName: "Country",
      flex: 1.2,
      minWidth: 130,
      type: "singleSelect",
      valueOptions: countries.map((c) => ({ value: String(c.id), label: c.name })),
      filterOperators: selectCustomOperators,
      valueGetter: (params) => {
        const countriesArr = params.row.countries;
        if (!countriesArr || countriesArr.length === 0) return "";
        return countriesArr.map((c) => c.name).join(", ");
      },
      sortable: false,
      hideable:false
    },
    {
      field: "category",
      headerName: "Categories",
      flex: 1.3,
      minWidth: 140,
      type: "singleSelect",
      valueOptions: categories.map((c) => ({ value: c.name, label: c.name })),
      valueGetter: (params) => {
        const assignments = params.row.categoryAssignments;
        if (!assignments || assignments.length === 0) return "";
        return [...new Set(assignments.map((a) => a.category?.name).filter(Boolean))].join(", ");
      },
      sortable: false,
      hideable:false
    },
    {
      field: "assignedUserName",
      headerName: "Assignees",
      flex: 1.5,
      minWidth: 160,
      type: "string",
      filterOperators: stringCustomOperators,
      valueGetter: (params) => {
        const assignments = params.row.categoryAssignments;
        if (!assignments || assignments.length === 0) return "";
        return [...new Set(assignments.map((a) => a.assignedUser?.username || a.assignedUser?.email).filter(Boolean))].join(", ");
      },
      sortable: false,
      hideable:false 
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1.2,
      minWidth: 120,
      type: "date",
      valueGetter: (params) =>
        params.row.createdAt ? new Date(params.row.createdAt) : null,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "primaryDate",
      headerName: "Primary Date",
      flex: 1.2,
      minWidth: 120,
      type: "date",
      valueGetter: (params) =>
        params.row.primaryDate ? new Date(params.row.primaryDate) : null,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "secondaryDate",
      headerName: "Secondary Date",
      flex: 1.2,
      minWidth: 120,
      type: "date",
      valueGetter: (params) =>
        params.row.secondaryDate ? new Date(params.row.secondaryDate) : null,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "ulInternalDate",
      headerName: "UL Internal Date",
      flex: 1.2,
      minWidth: 120,
      type: "date",
      valueGetter: (params) =>
        params.row.ulInternalDate ? new Date(params.row.ulInternalDate) : null,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.5,
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.row.status?.replace(/_/g, " ")}
          color={getStatusColor(params.row.status)}
          size="small"
        />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "numberOfRemainingResponses",
      headerName: "Impact Assessments Remaining",
      flex: 1,
      minWidth: 100,
      type: "number",
      filterOperators: numberCustomOperators,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 150,
      minWidth: 120,
      maxWidth: 150,
      disableReorder: true,
    },
  ];
  const initialColumnVisibilityModel = {
    numberOfRemainingResponses: false,
    createdAt: false,
    ingredientCasNumber: false,
    ingredientName: false,
    countryId: false,
    category: false,
    assignedUserName: false,
    secondaryDate: false,
    ulInternalDate: false,
  };
  const renderActions = (params, navigate, theme) => (
    <ThemeProvider theme={theme}>
      {!all && (
        <Tooltip title="Actions">
          <MeatballsMenu
            options={meatballsMenuOptions(params.row.id, params.row.status)}
          />
        </Tooltip>
      )}
      {all && (
        <Tooltip title="Preview Request PDF">
          <IconButton
            color="info"
            onClick={() => openPdfPopup(params.row.id)}
            size="small"
          >
            <PictureAsPdfIcon />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Preview Request">
        <IconButton
          color="info"
          onClick={() => {
            if (user.role === "APPROVER" && title === "Pending Approvals") {
              navigate(
                DefaultRoutes.ApproverRequestInfo.replace(
                  ":reqId",
                  params.row.id,
                ),
              );
            } else {
              navigate(
                DefaultRoutes.myReqInfo.replace(":reqId", params.row.id),
              );
            }
          }}
          size="small"
        >
          {user.role === "APPROVER" && title === "Pending Approvals" ? (
            <PlayArrowRoundedIcon />
          ) : (
            <LightbulbOutlinedIcon />
          )}
        </IconButton>
      </Tooltip>
    </ThemeProvider>
  );

  const handleAddReqButton = () => {
    navigate(DefaultRoutes.createIngredientExit);
  };

  return (
    <GenericExitRequestListing
      title={title}
      PageIcon={<FolderSpecialIcon color="white" fontSize="large" />}
      iconColor="primary.main"
      apiRoute={EXIT_REQUESTS}
      columns={columns}
      renderActions={renderActions}
      errorMessage="Failed to Fetch My Exit Requests Trying Again!"
      buttons={
        !readOnly
          ? [
              <AddButton
                key="add-button"
                ButtonText={`Add New Exit Request`}
                onClick={handleAddReqButton}
              />,
            ]
          : undefined
      }
      extraComponents={
        <>
          {DeletePopup(
            "Exit Request",
            showCancelPopup,
            closePopup,
            ConfirmCancel,
            selectedId,
            cancelReqLoading,
            "Cancel",
            cancelWarningContent,
          )}
          <Dialog
            open={showPdfPopup}
            onClose={closePdfPopup}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                height: "90vh",
                maxHeight: "90vh",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: "white",
                color: "white",
              }}
            >
              Exit Request PDF
              <IconButton
                onClick={closePdfPopup}
                sx={{ color: "white" }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, height: "100%" }}>
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title="PDF Preview"
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    gap: 2,
                  }}
                >
                  <CircularProgress size={60} />
                  <Box sx={{ textAlign: "center" }}>
                    <h3 style={{ margin: 0, color: "#666" }}>Loading PDF...</h3>
                    <p style={{ margin: "8px 0 0 0", color: "#999" }}>
                      Please wait while we fetch your document
                    </p>
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                bgcolor: "grey.50",
                borderTop: "1px solid",
                borderColor: "grey.300",
                p: 2,
              }}
            >
              <Button
                onClick={handleDownloadFromPopup}
                variant="contained"
                startIcon={<DownloadIcon />}
                color="primary"
                disabled={!pdfUrl || pdfLoading}
              >
                {pdfLoading ? "Loading..." : "Download PDF"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      }
      extraProps={{ updateRows: setRows }}
      fetchPage={fetchPage}
      rows={rows}
      pageLoading={pageLoading}
      rowCount={rowCount}
      initialColumnVisibilityModel={initialColumnVisibilityModel}
      exportApi={exportUrl}
    />
  );
}

export default GenericPendingExitRequests;
